import React from "react";
import SEO from "../../components/common/SEO";
import {
  Rocket,
  UserPlus,
  Mail,
  TrendingDown,
  Zap,
  CheckCircle,
  ArrowRight,
  Shield,
  Activity,
  Sparkles,
} from "lucide-react";

export default function SaaS({ user, authToken }) {
  return (
    <>
      <SEO
        title="Email Validation for SaaS - VALIDMAIL"
        description="Improve activation rates, reduce churn, and protect transactional emails. Real-time validation for SaaS signups and lifecycle emails."
        keywords="SaaS email validation, signup validation, transactional email, user activation"
        canonicalUrl="https://validmail.io/solutions/saas"
      />

      <div className="min-h-screen bg-white dark:bg-slate-950 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
              For SaaS Product Teams
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-slate-50 mb-4 sm:mb-6">
              Build on a foundation of{" "}
              <span className="text-purple-600 dark:text-purple-400">valid emails</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Improve activation rates, reduce churn, and protect your transactional email channels with real-time validation for signups and invites.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-12 md:mb-16">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-purple-200 dark:border-purple-800 text-center">
              <Rocket className="h-8 w-8 sm:h-10 sm:w-10 text-purple-600 dark:text-purple-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">+35%</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Activation Rate</p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-emerald-200 dark:border-emerald-800 text-center">
              <TrendingDown className="h-8 w-8 sm:h-10 sm:w-10 text-emerald-600 dark:text-emerald-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">-68%</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Email Churn</p>
            </div>

            <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-sky-200 dark:border-sky-800 text-center">
              <Mail className="h-8 w-8 sm:h-10 sm:w-10 text-sky-600 dark:text-sky-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">99.2%</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Deliverability</p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-amber-200 dark:border-amber-800 text-center">
              <Activity className="h-8 w-8 sm:h-10 sm:w-10 text-amber-600 dark:text-amber-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">+45%</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Engagement</p>
            </div>
          </div>

          <div className="mb-16 md:mb-24">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-50 text-center mb-8 sm:mb-12">
              How VALIDMAIL fits your product
            </h2>
            <div className="relative">
              <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-purple-200 via-purple-400 to-purple-200 dark:from-purple-800 dark:via-purple-600 dark:to-purple-800 -translate-y-1/2"></div>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-4 relative">
                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-purple-200 dark:border-purple-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    1
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Signup</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    User enters email during account creation
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-purple-200 dark:border-purple-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    2
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Validation</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Real-time check before account is created
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-purple-200 dark:border-purple-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    3
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Onboarding</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Send welcome and activation emails with confidence
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-purple-200 dark:border-purple-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    4
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Lifecycle</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Nurture users with feature updates and tips
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-purple-200 dark:border-purple-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    5
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Expansion</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Drive upgrades and referrals through email
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16 md:mb-24">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-50 text-center mb-8 sm:mb-12">
              Built for product teams
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-purple-200 dark:border-purple-800">
                <UserPlus className="h-10 w-10 sm:h-12 sm:w-12 text-purple-600 dark:text-purple-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Validate Signups & Invites
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Stop bad emails at signup. Validate user registrations and in-app invites in real-time to ensure clean data from day one.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Instant API validation during signup flow</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Catch typos and disposable emails immediately</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Validate team invites before sending</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-sky-200 dark:border-sky-800">
                <Mail className="h-10 w-10 sm:h-12 sm:w-12 text-sky-600 dark:text-sky-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Reduce Bounced Lifecycle Emails
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Ensure critical onboarding, activation, and re-engagement emails reach your users. No more bounced welcome emails.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-sky-600 dark:text-sky-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Higher activation rates with valid emails</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-sky-600 dark:text-sky-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Successful password resets and notifications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-sky-600 dark:text-sky-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Better user experience and retention</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-emerald-200 dark:border-emerald-800">
                <Shield className="h-10 w-10 sm:h-12 sm:w-12 text-emerald-600 dark:text-emerald-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Protect Transactional Email
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Maintain your sender reputation and ensure critical transactional emails always reach their destination.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Prevent bounce-related IP blacklisting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Maintain high deliverability scores</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Reduce email infrastructure costs</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-amber-200 dark:border-amber-800">
                <Zap className="h-10 w-10 sm:h-12 sm:w-12 text-amber-600 dark:text-amber-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Fast API Integration
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Simple REST API that integrates with your tech stack in minutes. SDKs for all major languages.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Under 200ms average response time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Node.js, Python, PHP, Ruby SDKs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Comprehensive API documentation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-2xl sm:rounded-3xl p-8 sm:p-12 border-2 border-slate-200 dark:border-slate-700 mb-16 md:mb-24">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs sm:text-sm font-medium mb-4">
                Case Study
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-50 mb-4">
                SaaS platform reduces email churn by 68%
              </h3>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-6">
                A B2B SaaS company with 10,000+ users was experiencing high bounce rates on critical lifecycle emails. After integrating VALIDMAIL at signup and validating their existing user base:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-purple-600 dark:text-purple-400 mb-1">68%</div>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Reduction in email churn</p>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-purple-600 dark:text-purple-400 mb-1">35%</div>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Increase in activation rate</p>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-purple-600 dark:text-purple-400 mb-1">99.2%</div>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Email deliverability achieved</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center text-white shadow-2xl">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Start building on clean data
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-90 max-w-2xl mx-auto">
              Integrate our API in minutes. Start with 100 free validation credits.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={() => window.location.href = authToken ? "/product/api" : "/signin"}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold bg-white text-purple-600 hover:bg-slate-50 transition-all shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2"
              >
                {authToken ? "View API Docs" : "Get Started Free"}
                <ArrowRight className="h-5 w-5" />
              </button>
              <button
                onClick={() => window.location.href = "/pricing"}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold border-2 border-white text-white hover:bg-white hover:text-purple-600 transition-all"
              >
                View Pricing
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
